// covered-by: frontends/carrie/src/components/ask-puntt-thread/__tests__/threadVisibility.test.ts
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";

type VisibilityMessage = Pick<
  AskPunttMessage,
  | "role"
  | "content"
  | "streamingContent"
  | "agentSteps"
  | "searchProgress"
  | "contentBlocks"
  | "components"
>;

/**
 * Per-ticket "review underway" rows: empty content + only thought/text
 * agent steps. They look pending but are complete server-side events.
 */
export const isReviewStatusMessage = (message: VisibilityMessage): boolean =>
  message.role === "assistant" &&
  message.content === "" &&
  (message.agentSteps?.length ?? 0) > 0 &&
  (message.agentSteps ?? []).every(
    (step) =>
      (step as { tool?: string; type?: string }).tool === "thought" ||
      (step as { tool?: string; type?: string }).type === "text",
  );

/** Assistant shell with no visible body yet (pre-stream placeholder). */
export const isEmptyAssistantPlaceholder = (
  message: VisibilityMessage,
): boolean => {
  if (message.role !== "assistant" || isReviewStatusMessage(message)) {
    return false;
  }
  return (
    !(message.content || message.streamingContent) &&
    !(message.agentSteps?.length ?? 0) &&
    !(message.searchProgress?.length ?? 0) &&
    !(message.contentBlocks?.length ?? 0) &&
    // why: drawn chat atoms are the turn body even when content is still empty
    !(message.components?.length ?? 0)
  );
};

/**
 * Whether an empty assistant shell should be omitted from the thread.
 *
 * Feed always skips these — thinking UI renders below the list. Chat keeps
 * the last empty row (the live "Starting up..." turn) but hides earlier
 * orphans left by retried create_placeholder_message_activity.
 */
export const shouldHideEmptyAssistantPlaceholder = ({
  message,
  isLast,
  presentation,
}: {
  message: VisibilityMessage;
  isLast: boolean;
  presentation: "chat" | "feed";
}): boolean => {
  if (!isEmptyAssistantPlaceholder(message)) return false;
  if (presentation === "feed") return true;
  return !isLast;
};
